import { useEffect, useState } from "react";
import getPotentialTest from "../restful/getPotentialTest";
import useCountdown from "./useCountdown";
import CryptoJS from "crypto-js";
import getPotensiKerja from "../restful/getPotensiKerja";
import getTesAkademik from "../restful/getTesAkademik";
import getSpecialPotentialTest from "../restful/getSpecialPotentialTest";

const getLocalSession = () => {
    const raw = localStorage.getItem("session");
    if (!raw) return null;
    try {
        const decrypted = CryptoJS.AES.decrypt(raw, "secret").toString(
            CryptoJS.enc.Utf8
        );
        return JSON.parse(decrypted);
    } catch (e) {
        return null;
    }
};

const getCurrentToken = () => {
    try {
        const raw = JSON.parse(localStorage.getItem("authUser"));
        return raw?.token || raw?.user?.token || "";
    } catch (e) {
        return "";
    }
};

const getCurrentId = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get("id") || "";
    } catch (e) {
        return "";
    }
};

const isSessionValid = (localSession, data, currentVersion) => {
    if (!localSession || !localSession.session) return false;
    const sessionIdx = localSession.session.session;
    if (typeof sessionIdx !== "number" || isNaN(sessionIdx)) return false;
    if (data && data.length > 0) {
        if (sessionIdx >= data.length || sessionIdx < 0) return false;
    }
    const currentToken = getCurrentToken();
    if (localSession.token && currentToken && localSession.token !== currentToken) {
        return false;
    }
    const currentId = getCurrentId();
    if (localSession.id && currentId && localSession.id !== currentId) {
        return false;
    }
    if (localSession.version && currentVersion && localSession.version !== currentVersion) {
        return false;
    }
    return true;
};

export default function useSession(version, onStart) {
    const initialLocalSession = getLocalSession();
    const isInitialValid = isSessionValid(initialLocalSession, null, version);
    const initialDuration = isInitialValid && typeof initialLocalSession.duration === "number" && initialLocalSession.duration > 0
        ? initialLocalSession.duration
        : 0;

    const { duration, time, setDuration, start } = useCountdown(initialDuration);
    const [session, setSession] = useState([]);
    const [current, setCurrent] = useState(() => {
        return (isInitialValid && initialLocalSession?.session) ? initialLocalSession.session : {
            session: null,
            soal: null,
        };
    });

    useEffect(() => {
        // Do NOT overwrite localStorage if session data is not loaded yet,
        // or current session is null, or duration is invalid/zero
        if (!session || session.length === 0) return;
        if (current.session === null || current.session === undefined) return;
        if (typeof duration !== "number" || isNaN(duration) || duration <= 0) return;

        try {
            const currentToken = getCurrentToken();
            const currentId = getCurrentId();
            localStorage.setItem(
                "session",
                CryptoJS.AES.encrypt(
                    JSON.stringify({
                        session: current,
                        duration,
                        token: currentToken,
                        id: currentId,
                        version,
                    }),
                    "secret"
                ).toString()
            );
        } catch (e) {
            console.error("Failed to save session to localStorage:", e);
        }
    }, [duration, current, session, version]);

    const applySessionData = (data, localSession) => {
        if (!data || data.length === 0) return;
        setSession(data);

        // Fetch fresh stored session from localStorage
        const storedLocal = getLocalSession();
        let validLocal = isSessionValid(storedLocal, data, version)
            ? storedLocal
            : (isSessionValid(localSession, data, version) ? localSession : null);

        if (!validLocal && (storedLocal || localSession)) {
            localStorage.removeItem("session");
            localStorage.removeItem("answer");
            localStorage.removeItem("mapping");
        }

        let currentData = validLocal
            ? validLocal.session
            : {
                session: 0,
                soal: data[0]?.type === "exam" ? 0 : null,
            };

        // Safety clamp: ensure current session index is within bounds
        if (typeof currentData.session !== "number" || currentData.session >= data.length || currentData.session < 0) {
            currentData.session = 0;
            currentData.soal = data[0]?.type === "exam" ? 0 : null;
        }

        const currentSessionObj = data[currentData.session] || data[0];
        const defaultDuration = Number(currentSessionObj?.duration) || 60000;
        const targetDuration = validLocal && typeof validLocal.duration === "number" && validLocal.duration > 0
            ? Math.min(validLocal.duration, defaultDuration > 0 ? defaultDuration : validLocal.duration)
            : (defaultDuration > 0 ? defaultDuration : 60000);

        setCurrent(currentData);
        setDuration(targetDuration);
        start();

        // ONLY trigger onStart (modal instruksi) if the current session is strictly an "instruction" session AND user has no saved answers!
        const isInstruction = data[currentData.session]?.type === "instruction";
        const hasAnswers = (() => {
            try {
                const rawAns = localStorage.getItem('answer');
                if (!rawAns) return false;
                const dec = CryptoJS.AES.decrypt(rawAns, 'secret').toString(CryptoJS.enc.Utf8);
                const parsed = JSON.parse(dec);
                return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
            } catch (e) {
                return false;
            }
        })();

        if (isInstruction && !hasAnswers) {
            if (!validLocal || currentData.session === 0) {
                onStart();
            }
        }
    };

    useEffect(() => {
        let localSession = getLocalSession();
        let urlParams = new URLSearchParams(window.location.search);
        switch (version) {
            case "potential-test":
                getPotentialTest(version)
                    .then((data) => applySessionData(data, localSession))
                    .catch((error) => console.error("Error loading PT:", error));
                break;
            case "potensi-kerja":
                getPotensiKerja(version, urlParams.get("id"))
                    .then((data) => applySessionData(data, localSession))
                    .catch((error) => console.error("Error loading PK:", error));
                break;
            case "tes-akademik": {
                const currentMapelId = urlParams.get("id");
                const lastMapelId = localStorage.getItem("last_ta_mapel_id");
                if (currentMapelId && lastMapelId !== currentMapelId) {
                    localStorage.removeItem('session');
                    localStorage.removeItem('answer');
                    localStorage.removeItem('mapping');
                    localSession = null;
                    localStorage.setItem("last_ta_mapel_id", currentMapelId);
                }

                getTesAkademik(version, currentMapelId)
                    .then((data) => {
                        if (!data || data.length === 0) {
                            console.error('No session data found for Tes Akademik');
                            return;
                        }
                        applySessionData(data, localSession);
                    })
                    .catch((error) => console.error("Error loading TA:", error));
                break;
            }
            case "spesial-potential-test":
                getSpecialPotentialTest(version, urlParams.get("id"))
                    .then((data) => applySessionData(data, localSession))
                    .catch((error) => console.error('Error loading SPT session:', error));
                break;
            default:
                break;
        }
    }, []);

    return {
        session: {
            data: session,
            set: setSession,
        },
        current: {
            data: current,
            set: setCurrent,
        },
        countdown: {
            duration,
            time,
            setDuration,
            start,
        },
    };
}
